import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAlbumContext } from '@/contexts/AlbumProvider';
import { useSettings } from '@/hooks/useSettings';
import { isUserActive } from '@/lib/utils';

interface UserWithRole {
  id: string;
  email: string;
  user_metadata: {
    name?: string;
    picture?: string;
    full_name?: string;
    avatar_url?: string;
  };
  last_sign_in_at: string;
  role?: string;
  is_banned: boolean;
  last_seen_at?: string;
  last_active_at?: string;
}

interface ActiveUsersResult {
  users: UserWithRole[];
  activeCount: number;
  totalCount: number;
  loading: boolean;
  refreshUsers: () => Promise<void>;
}

export const useAlbumUsers = (): ActiveUsersResult => {
  const { albumId } = useAlbumContext();
  const { getBooleanSetting } = useSettings();
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [loading, setLoading] = useState(true);


  const fetchUsers = useCallback(async () => {
    try {
      // Use the RPC function to get users with roles for the current album
      const { data, error } = await supabase
        .rpc('get_users_with_roles', { album_id_param: albumId });

      if (error) throw error;

      // Map the data to our interface
      const userData: UserWithRole[] = (data || []).map((user: any) => ({
        id: user.id,
        email: user.email,
        user_metadata: user.user_metadata || {},
        last_sign_in_at: user.last_sign_in_at,
        role: user.role,
        is_banned: user.is_banned || false,
        last_seen_at: user.last_seen_at,
        last_active_at: user.last_seen_at // This field comes from the database function
      }));

      // Sort users by last login (most recent first)
      const sortedUsers = userData.sort((a, b) => {
        const dateA = new Date(a.last_sign_in_at || 0);
        const dateB = new Date(b.last_sign_in_at || 0);
        return dateB.getTime() - dateA.getTime();
      });

      setUsers(sortedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [albumId]);

  useEffect(() => {
    fetchUsers();

    // Set up interval to refresh users every 30 seconds to get updated active status
    const interval = setInterval(fetchUsers, 10000);

    return () => clearInterval(interval);
  }, [fetchUsers]);

  // Calculate active users based on last_active_at timestamp
  // Filter out superadmin users if the setting is false
  const countSuperadmins = getBooleanSetting('count_superadmins_in_widget', true);
  const filteredUsers = countSuperadmins ? users : users.filter(user => user.role !== 'superadmin');

  const activeCount = filteredUsers.filter(user => isUserActive(user.last_active_at)).length;
  const totalCount = filteredUsers.length;

  return {
    users,
    activeCount,
    totalCount,
    loading,
    refreshUsers: fetchUsers
  };
};